import { Kundetype, Produkt } from "codegen";

import useProducts from "hooks/useProducts";
import { Box, FieldError, FieldLabel, Grid, Hint, Input, Label, Select, StepHeading, StepWrapper } from "pages/Sikring/opprett/CreateSikringPage";
import { WizardState, skipsSigning } from "pages/Sikring/opprett/wizardTypes";
import { INVALID_NUMBER_MESSAGE, PRICE_UNIT, exceedsPriceDigits, isInvalidNumber, parseNorwegianNumber } from "pages/Sikring/sikringUtils";
import AddressSearchTextField, { geoNorgeAddressToMaalerAdresse, validateFakturaAdresse } from "pages/Strombestilling/orders/components/AddressSearchTextField";
import PersonSearch from "pages/Strombestilling/orders/components/PersonSearch";
import { formatAddress } from "utils/Address";
import { getEmailValidationError } from "utils/EmailValidator";
import { validateFodselsnummer } from "utils/Utils";

interface Step2Props {
	stepNumber: number;
	wiz: WizardState;
	update: <K extends keyof WizardState>(field: K, value: WizardState[K]) => void;
	/** Oppfølgingsproduktet slik det er hentet, med interaktive felter. Undefined mens det hentes. */
	followupProduct?: Produkt;
	followupProductLoading: boolean;
	followupProductFailed: boolean;
}

/**
 * Kontaktperson og fakturaadresse fylt ut (fødselsnummer bare når kunden skal signere), gyldig påslag og fastbeløp på hovedproduktet, oppfølgingsprodukt
 * valgt og hentet, og et gyldig tall i hvert av oppfølgingsproduktets interaktive felter.
 */
export function getMissingFieldsForStep2(wiz: WizardState, followupProduct?: Produkt): string[] {
	const missing: string[] = [];
	const contactComplete =
		!!wiz.contactFirstName.trim() &&
		!!wiz.contactLastName.trim() &&
		!!wiz.contactMobile.trim() &&
		getEmailValidationError(wiz.contactEmail) === null &&
		(skipsSigning(wiz) || validateFodselsnummer(wiz.contactFodselsnummer));
	if (!contactComplete) missing.push("Fyll ut kontaktpersonen.");
	if (!wiz.invoiceAddress || validateFakturaAdresse(wiz.invoiceAddress) !== null) missing.push("Velg en gyldig fakturaadresse.");
	if (!isValidPaaslag(wiz.paaslag)) missing.push("Fyll ut påslag.");
	if (!isValidFastbelop(wiz.fastbelop)) missing.push("Fyll ut fastbeløp.");
	if (!wiz.followupTariffId) {
		missing.push("Velg oppfølgingsprodukt.");
	} else if (followupProduct?.tariffId !== wiz.followupTariffId) {
		missing.push("Venter på prisfeltene for oppfølgingsproduktet.");
	} else if (!getInteractiveFields(followupProduct).every((field) => parseNorwegianNumber(wiz.interactiveFieldValues[field]) !== null)) {
		missing.push("Fyll ut prisfeltene for oppfølgingsproduktet.");
	}
	return missing;
}

// Påslaget legges på spotprisen, så et negativt påslag er en tastefeil.
function isValidPaaslag(paaslag: string): boolean {
	const value = parseNorwegianNumber(paaslag);
	return value !== null && value >= 0;
}

// Et negativt fast beløp er en tastefeil, som for påslaget.
function isValidFastbelop(fastbelop: string): boolean {
	const value = parseNorwegianNumber(fastbelop);
	return value !== null && value >= 0;
}

export function getInteractiveFields(product?: Produkt): string[] {
	return product?.interaktiveFelter ?? [];
}

/**
 * i:Margin_prissikring_pris -> Margin prissikring pris (Oppf.produkt). Feltnavnene fra UtilityCloud har ingen egen
 * visningstekst. Produktet er markert, så et felt som Paaslag ikke forveksles med påslaget på hovedproduktet.
 */
export function interactiveFieldLabel(field: string): string {
	return `${field.replace(/^i:/, "").replaceAll("_", " ")} (Oppf.produkt)`;
}

export function Step2Contract({ stepNumber, wiz, update, followupProduct, followupProductLoading, followupProductFailed }: Readonly<Step2Props>) {
	const {
		productSelectOptions: followupOptions,
		isLoadingProducts,
		productsError,
	} = useProducts({
		kundetype: Kundetype.Bedrift,
		mainProductsOnly: true,
	});
	const invoiceAddressError = wiz.invoiceAddress ? validateFakturaAdresse(wiz.invoiceAddress) : "Velg en fakturaadresse fra nedtrekksmenyen.";
	const interactiveFields = followupProduct?.tariffId === wiz.followupTariffId ? getInteractiveFields(followupProduct) : [];

	return (
		<StepWrapper $gap="14px">
			<StepHeading>Steg {stepNumber} – Kontrakt</StepHeading>

			<Box>
				<Label style={{ marginBottom: "4px" }}>Kontaktperson</Label>
				<Hint style={{ margin: "0 0 12px" }}>
					Kontrakten krever en kontaktperson. Bedriftssøket gir bare organisasjonsnummer, navn og adresse. Fakturaadressen blir bedriftsadressen, men
					kan endres.
				</Hint>
				<Grid>
					<div style={{ gridColumn: "1 / -1" }}>
						{skipsSigning(wiz) ? (
							<>
								<FieldLabel>Fødselsnummer</FieldLabel>
								<Hint style={{ margin: "6px 0 0" }}>Trengs bare når kunden skal signere.</Hint>
							</>
						) : (
							<PersonSearch
								fodselsnummer={wiz.contactFodselsnummer || undefined}
								setFodselsnummer={(fodselsnummer) => update("contactFodselsnummer", fodselsnummer ?? "")}
								onPersonFound={(person) => {
									update("contactFirstName", person.fornavn ?? "");
									update("contactLastName", person.etternavn ?? "");
								}}
							/>
						)}
					</div>
					<div>
						<FieldLabel>Fornavn</FieldLabel>
						<Input type="text" value={wiz.contactFirstName} onChange={(e) => update("contactFirstName", e.target.value)} autoComplete="off" />
					</div>
					<div>
						<FieldLabel>Etternavn</FieldLabel>
						<Input type="text" value={wiz.contactLastName} onChange={(e) => update("contactLastName", e.target.value)} autoComplete="off" />
					</div>
					<div>
						<FieldLabel>Mobil</FieldLabel>
						<Input
							type="text"
							inputMode="numeric"
							value={wiz.contactMobile}
							onChange={(e) => update("contactMobile", e.target.value)}
							autoComplete="off"
						/>
					</div>
					<div>
						<FieldLabel>E-post</FieldLabel>
						<Input type="email" value={wiz.contactEmail} onChange={(e) => update("contactEmail", e.target.value)} autoComplete="off" />
					</div>
					<div style={{ gridColumn: "1 / -1" }}>
						<FieldLabel>Fakturaadresse</FieldLabel>
						{/* Feltet er ukontrollert, men steget monteres på nytt hver gang det vises, så adressen i wiz leses inn igjen. */}
						<AddressSearchTextField
							defaultValue={formatAddress(wiz.invoiceAddress)}
							setAddress={(address) => update("invoiceAddress", address ? geoNorgeAddressToMaalerAdresse(address) : null)}
						/>
						{invoiceAddressError && <FieldError>{invoiceAddressError}</FieldError>}
					</div>
				</Grid>
			</Box>

			<Box>
				<Label style={{ marginBottom: "4px" }}>Hovedprodukt</Label>
				<Grid>
					<div>
						<FieldLabel htmlFor="paaslag">Påslag ({PRICE_UNIT} eks. mva)</FieldLabel>
						<Input
							id="paaslag"
							type="text"
							inputMode="decimal"
							autoComplete="off"
							value={wiz.paaslag}
							onChange={(e) => !exceedsPriceDigits(e.target.value) && update("paaslag", e.target.value)}
						/>
						{isInvalidNumber(wiz.paaslag) ? (
							<FieldError>{INVALID_NUMBER_MESSAGE}</FieldError>
						) : (
							wiz.paaslag.length > 0 && !isValidPaaslag(wiz.paaslag) && <FieldError>Påslaget kan ikke være negativt.</FieldError>
						)}
					</div>
					<div>
						<FieldLabel htmlFor="fastbelop">Fastbeløp (kr eks. mva)</FieldLabel>
						<Input
							id="fastbelop"
							type="text"
							inputMode="decimal"
							autoComplete="off"
							value={wiz.fastbelop}
							onChange={(e) => !exceedsPriceDigits(e.target.value) && update("fastbelop", e.target.value)}
						/>
						{isInvalidNumber(wiz.fastbelop) ? (
							<FieldError>{INVALID_NUMBER_MESSAGE}</FieldError>
						) : (
							wiz.fastbelop.length > 0 && !isValidFastbelop(wiz.fastbelop) && <FieldError>Fastbeløpet kan ikke være negativt.</FieldError>
						)}
					</div>
				</Grid>
			</Box>

			<Box>
				<Label style={{ marginBottom: "4px" }}>Oppfølgingsprodukt</Label>
				<Hint style={{ margin: "0 0 12px" }}>Produktet kunden går over til når sikringen er ferdig.</Hint>
				<Select
					value={wiz.followupTariffId}
					disabled={isLoadingProducts}
					onChange={(e) => {
						update("followupTariffId", e.target.value);
						// Verdiene hører til forrige produkt. Et felt med samme navn på det nye produktet skal fylles ut på nytt.
						update("interactiveFieldValues", {});
					}}
					aria-label="Oppfølgingsprodukt"
				>
					<option value="">{isLoadingProducts ? "Henter produkter …" : "Velg produkt"}</option>
					{followupOptions.map((p) => (
						<option key={p.value} value={p.value}>
							{p.label}
						</option>
					))}
				</Select>
				{productsError && <FieldError>Klarte ikke å hente produktene.</FieldError>}
				{wiz.followupTariffId && followupProductLoading && <Hint>Henter prisfelter for produktet …</Hint>}
				{wiz.followupTariffId && followupProductFailed && <FieldError>Klarte ikke å hente prisfeltene for produktet.</FieldError>}

				{interactiveFields.length > 0 && (
					<Grid style={{ marginTop: "14px" }}>
						{interactiveFields.map((field) => {
							const value = wiz.interactiveFieldValues[field] ?? "";
							return (
								<div key={field}>
									<FieldLabel htmlFor={`interaktivt-felt-${field}`}>{interactiveFieldLabel(field)}</FieldLabel>
									<Input
										id={`interaktivt-felt-${field}`}
										type="text"
										inputMode="decimal"
										autoComplete="off"
										value={value}
										onChange={(e) =>
											!exceedsPriceDigits(e.target.value) &&
											update("interactiveFieldValues", { ...wiz.interactiveFieldValues, [field]: e.target.value })
										}
									/>
									{isInvalidNumber(value) && <FieldError>{INVALID_NUMBER_MESSAGE}</FieldError>}
								</div>
							);
						})}
					</Grid>
				)}
			</Box>
		</StepWrapper>
	);
}
